/* ============================================================================
 * Weather Sicily Maps v2 — help "?" e "Cosa sto vedendo?".
 * Port da assets/js/map.js (~righe 3178-3340):
 *  - LAYER_HELP: id checkbox → [nome semplice, spiegazione+fonte]. Testi
 *    VERBATIM dalla v1; gli id sono rimappati sugli id dei toggle v2
 *    (ovl-, drv-, risk-, me-, ana-). Le voci senza corrispettivo v2
 *    mantengono l'id v1 (chkForecast: serviranno quando il layer arriverà).
 *  - initHelp(): bottone "❓ Cosa sto vedendo?" in cima a #layersBody che
 *    elenca i SOLI layer attivi + delega click su .ws-help/[data-ws-help]
 *    con popup .wsv2-help-pop (il title nativo non funziona su mobile).
 * ==========================================================================*/

// Dizionario: id checkbox (v2) -> [nome semplice, spiegazione]. Il "?" centrale
// mostra SOLO i layer attivi in quel momento → "capisco al volo cosa sto vedendo".
export const LAYER_HELP = {
    chkRadar: ["Radar pioggia", "Mostra la distribuzione e l’intensità delle precipitazioni rilevate dal radar."],
    chkSatellite: ["Satellite nubi", "Copertura nuvolosa vista dal satellite."],
    chkTemporali: ["Temporali", "Celle temporalesche tracciate e percorso previsto."],
    chkLightning: ["Fulmini", "Fulmini in tempo reale."],
    'ovl-dust': ["Polveri sahariane", "Polvere sahariana in atmosfera vista dal satellite (appare rosa/magenta)."],
    'ovl-solar': ["Energia solare (fotovoltaico) ☀️", "Quanta energia solare riceve ogni zona in un anno (kWh/m²), misurata da satellite: indica quanto rende un impianto fotovoltaico in quel punto."],
    'ovl-fwi': ["Pericolo incendi (FWI)", "Quanto il meteo dei prossimi giorni favorisce gli incendi."],
    'risk-etna': ["Stato Etna", "Voli (codice colore), cenere e calore dell'Etna. Fonti: INGV / VAAC / MIROVA."],
    'ovl-snow': ["Neve al suolo", "Copertura nevosa vista dal satellite (MODIS): utile su Etna, Madonie e Nebrodi. Fonte: NASA GIBS."],
    'ovl-bathymetry': ["Profondità mare", "Profondità dei fondali (batimetria) attorno alla Sicilia: banchi del Canale di Sicilia, fosse dello Stretto. Contesto per onde, correnti e pesca. Fonte: EMODnet Bathymetry."],
    'risk-bathing': ["Balneazione + mare", "Qualità delle acque di ~750 spiagge siciliane (Eccellente/Buona/Sufficiente/Scarsa, riempimento del pallino) = classificazione ufficiale annuale EEA. L'ANELLO colorato indica lo stato del mare LIVE (calmo→molto mosso) dall'altezza delle onde. Fonti: European Environment Agency + Open-Meteo Marine."],
    'risk-rivers': ["Fiumi (portata)", "Portata prevista dei fiumi principali (quanta acqua scorre, m³/s): utile per cogliere le piene. D'estate i fiumi siciliani sono quasi secchi. Fonte: GloFAS via Open-Meteo."],
    chkSpaghetti: ["Spaghi modelli", "Spaghetti ensemble per ~45 località: ogni 'spago' è uno scenario possibile dei modelli (GFS/GEFS, ECMWF IFS e AIFS). Più gli spaghi sono vicini, più la previsione è affidabile. Clicca un punto per aprire i grafici. Fonte: ensemble WeatherSicily (Open-Meteo)."],
    'ovl-waves': ["Onde", "Altezza delle onde attorno alla Sicilia (modello)."],
    'ovl-sst': ["Temperatura mare", "Temperatura della superficie del mare."],
    'ovl-sstAnom': ["Anomalia SST", "Mare più caldo o freddo del normale: rosso = ondata di calore marina."],
    'risk-alerts': ["Allerte meteo-idro", "Allerte della Protezione Civile."],
    'risk-wildfireAlerts': ["Allerte incendi", "Allerte incendi della Protezione Civile Sicilia."],
    'risk-firms': ["Incendi live", "Incendi rilevati dai satelliti NASA."],
    'drv-quakes': ["Terremoti", "Terremoti recenti (INGV)."],
    'drv-territory': ['Strade e lavori', 'Avvisi CCISS con posizione ufficiale, sovrapponibili al meteo. Il punto indica l’inizio del tratto. Copertura parziale; non calcola ritardi.'],
    'drv-rainReports': ["Segnalazioni pioggia", "Cosa segnalano gli utenti vicino a te negli ultimi 45 minuti (crowdsourcing dalla card \"sta piovendo da te?\"). Il colore indica lo stato, l'opacità cala con il tempo. Tocca un punto per i dettagli."],
    'ana-rainAccum': ["Pioggia cumulata", "Pioggia totale caduta alle stazioni nelle ultime ore."],
    'ana-tempVariation': ["Variazione temperatura", "Di quanto è cambiata la temperatura rispetto a prima."],
    'drv-pollen': ["Pollini", "Livello dei pollini previsto (CAMS)."],
    'drv-air': ["Qualità aria", "Indice di qualità dell'aria (CAMS)."],
    'drv-airObs': ["Aria misurata", "Centraline reali a terra (NO₂/O₃/PM/SO₂/CO MISURATI, non modello): il pallino è colorato per l'indice europeo di qualità dell'aria, il popup mostra i valori e l'ora dell'ultima misura. Fonte: OpenAQ (reti ARPA). Da confrontare col layer \"Qualità aria\" modellato (CAMS)."],
    chkWind: ["Vento", "Direzione e intensità del vento alle stazioni."],
    'me-boe': ["Boe", "Boe meteo-marine."],
    'drv-tideGauges': ["Mareografi", "Livello del mare ai mareografi."],
    'me-ascat': ["Vento mare satellite", "Vento sulla superficie del mare MISURATO dal satellite (scatterometro ASCAT su Metop-B/-C, EUMETSAT OSI SAF). Riempie il vuoto dove non ci sono stazioni: il mare aperto. Le frecce puntano nel verso in cui soffia il vento, il colore indica l'intensità in km/h. Il satellite passa a strisce (~4 passaggi/giorno): la mappa fonde i passaggi delle ultime 48 ore per coprire Sicilia, Canale, Tirreno e Ionio. Dove il satellite non è ancora passato resta un vuoto, che si riempie coi passaggi successivi. Fonte: EUMETSAT OSI SAF · KNMI."],
    'me-s1wind': ["Vento mare HD (radar)", "Vento sul mare ad ALTA RISOLUZIONE (~1 km) ricavato dal radar SAR di Sentinel-1 (Copernicus/ESA). Vede il vento anche SOTTO COSTA, dove lo scatterometro ASCAT ha un vuoto a riva. Copertura a passaggi occasionali: su una stessa zona il radar ripassa solo ogni 2-3 giorni (lo Ionio aperto quasi ogni giorno, la Sicilia più di rado). La mappa fonde gli ultimi sorvoli (fino a ~5 giorni) e ogni freccia mostra la SUA data nel popup: quindi NON è il vento di adesso, ma quello dell'ultimo passaggio su quella zona. Frecce nel verso in cui soffia il vento, colore = intensità in km/h. Fonte: Copernicus Sentinel-1."],
    'me-ndbcShips': ["Navi NDBC", "Osservazioni meteo da navi (NOAA NDBC)."],
    'me-webcam': ["Webcam", "Webcam sul territorio."],
    chkMarkers: ["Stazioni", "Stazioni meteo: pallini con i valori misurati."],
    chkInterpol: ["Mappa colori", "Campo interpolato dei valori delle stazioni."],
    chkForecast: ["Previsione", "Campo previsto dal modello WSLam."],
    chkWslam: ["Modello WSLam", "Campi del modello meteo WSLam."],
    'risk-waterResources': ["Invasi", "Stato degli invasi e delle dighe."]
};

export function initHelp() {
    let pop = null;

    function close() {
        if (!pop) return;
        pop.remove(); pop = null;
        document.removeEventListener('click', onDoc, true);
        document.removeEventListener('keydown', onKey);
        window.removeEventListener('resize', close);
    }
    function onDoc(e) {
        if (pop && !pop.contains(e.target) && !e.target.closest('.ws-help, [data-ws-help]')) close();
    }
    function onKey(e) { if (e.key === 'Escape') close(); }

    function armAutoClose() {
        setTimeout(function () {
            document.addEventListener('click', onDoc, true);
            document.addEventListener('keydown', onKey);
            window.addEventListener('resize', close);
        }, 0);
    }

    function makePop(title, extraClass) {
        const p = document.createElement('div');
        p.className = 'wsv2-help-pop' + (extraClass ? ' ' + extraClass : '');
        p.setAttribute('role', 'dialog');
        p.innerHTML = '<div class="wsv2-help-pop__head"><span></span>'
            + '<button type="button" class="wsv2-help-pop__close" aria-label="Chiudi">×</button></div>'
            + '<div class="wsv2-help-pop__body"></div>';
        p.querySelector('.wsv2-help-pop__head span').textContent = title;
        document.body.appendChild(p);
        p.querySelector('.wsv2-help-pop__close').addEventListener('click', close);
        return p;
    }

    // --- Popover "?" singolo (badge .ws-help / [data-ws-help]) -------------
    function open(el) {
        close();
        const text = el.getAttribute('data-ws-help') || el.getAttribute('data-help') || el.getAttribute('title') || '';
        el.removeAttribute('title');
        pop = makePop('ℹ️ Cosa stai vedendo?');
        pop.querySelector('.wsv2-help-pop__body').textContent = text;
        if (window.innerWidth < 540) {
            pop.classList.add('wsv2-help-pop--sheet');
        } else {
            const r = el.getBoundingClientRect();
            const pw = pop.offsetWidth, ph = pop.offsetHeight;
            const left = Math.min(window.innerWidth - pw - 8, Math.max(8, r.left + r.width / 2 - pw / 2));
            let top = r.bottom + 8;
            if (top + ph > window.innerHeight - 8) top = Math.max(8, r.top - ph - 8);
            pop.style.left = left + 'px';
            pop.style.top = top + 'px';
        }
        armAutoClose();
    }

    // --- Pannellino "Cosa stai vedendo" (elenco SOLI layer attivi) ---------
    function openLayersHelp(btn) {
        close();
        const active = [];
        Object.keys(LAYER_HELP).forEach(id => {
            const cb = document.getElementById(id);
            if (cb && cb.checked) active.push(LAYER_HELP[id]);
        });
        let html;
        if (!active.length) html = '<p class="ws-help-empty">Nessun layer attivo. Attiva un layer dall\'elenco e qui troverai sempre la spiegazione di cosa stai guardando.</p>';
        else html = '<ul class="ws-help-list">' + active.map(a => '<li><b>' + a[0] + '</b><span>' + a[1] + '</span></li>').join('') + '</ul>';
        pop = makePop('👁️ Cosa stai vedendo', 'wsv2-panel wsv2-help-pop--layers');
        pop.querySelector('.wsv2-help-pop__body').innerHTML = html;
        if (window.innerWidth < 540) {
            pop.classList.add('wsv2-help-pop--sheet');
        } else {
            const r = btn.getBoundingClientRect(), pw = pop.offsetWidth, ph = pop.offsetHeight;
            pop.style.left = Math.min(window.innerWidth - pw - 8, Math.max(8, r.left)) + 'px';
            pop.style.top = (r.bottom + ph + 8 > window.innerHeight ? Math.max(8, r.top - ph - 8) : r.bottom + 8) + 'px';
        }
        armAutoClose();
    }

    function initOne(el) {
        if (el.dataset.helpReady) return;
        const t = el.getAttribute('title');
        if (t && !el.dataset.wsHelp && !el.dataset.help) { el.dataset.help = t; el.removeAttribute('title'); }
        el.dataset.helpReady = '1';
        el.style.cursor = 'pointer';
        if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
    }

    // --- Bottone in cima al pannello layer ----------------------------------
    const layersBody = document.getElementById('layersBody');
    if (layersBody && !document.getElementById('layers-help-btn')) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.id = 'layers-help-btn';
        btn.className = 'wsv2-help-btn';
        btn.textContent = '❓ Cosa sto vedendo?';
        btn.setAttribute('aria-label', 'Spiegazione dei layer attivi');
        btn.addEventListener('click', (e) => {
            e.preventDefault(); e.stopPropagation();
            openLayersHelp(btn);
        });
        layersBody.insertBefore(btn, layersBody.firstChild);
    }

    // --- Delega globale badge "?" -------------------------------------------
    document.querySelectorAll('.ws-help, [data-ws-help]').forEach(initOne);
    document.addEventListener('click', function (e) {
        const h = e.target.closest('.ws-help, [data-ws-help]');
        if (!h || h.id === 'layers-help-btn') return;
        e.preventDefault(); e.stopPropagation();
        initOne(h); open(h);
    });
    document.addEventListener('keydown', function (e) {
        const a = document.activeElement;
        if ((e.key !== 'Enter' && e.key !== ' ') || !a) return;
        if (a.id === 'layers-help-btn') return; // è un <button>: gestisce già Enter/Space
        if (a.classList.contains('ws-help') || a.hasAttribute('data-ws-help')) {
            e.preventDefault();
            initOne(a); open(a);
        }
    });
}
